import type { FieldFormatter } from '@lucidtech/las-sdk-browser';
import { ColumnDef } from '@tanstack/react-table';
import { memo, useMemo, useRef } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { BasicTooltip, Button, Flex, Label, Table, TableRef, Text, useComponentStack } from '@/components/core';
import { AddIcon } from '@/components/icons';
import {
  findField,
  FormatterCell,
  FormatterConfiguration,
  NavigationContainer,
  NewFormatterModal,
} from '@/components/ui';
import { useModelConfigStore } from '@/store';

export type FormattersSectionProps = {
  fieldId: string;
};

const IFormattersSection = ({ fieldId }: FormattersSectionProps) => {
  const field = useModelConfigStore((state) => findField(state.fields, fieldId));
  const moveFormatter = useModelConfigStore((state) => state.moveFormatter);
  const deleteFormatter = useModelConfigStore((state) => state.deleteFormatter);
  const formatterTableRef = useRef<TableRef<FieldFormatter>>(null);

  const componentStack = useComponentStack();

  const formatterColumns = useMemo<ColumnDef<FieldFormatter>[]>(() => {
    return [
      {
        accessorKey: 'functionId',
        cell: ({ row }) => (
          <FormatterCell
            formatter={row.original}
            onPress={() =>
              componentStack?.push(
                <NavigationContainer>
                  <FormatterConfiguration fieldId={fieldId} formatterId={row.original.id} />
                </NavigationContainer>
              )
            }
          />
        ),
        size: Number.MAX_SAFE_INTEGER,
      },
    ];
  }, [fieldId, componentStack]);

  const formatterData = useMemo(() => field?.formatters ?? [], [field]);
  const hasFormatters = useMemo(() => field?.formatters?.length ?? 0 > 0, [field]);

  if (!field) return;

  return (
    <section>
      <Flex col className="items-start">
        <Flex className="gap-sm w-full items-center pr-[1rem]">
          <Label>Formatters</Label>
          <BasicTooltip>
            <Text size="sm">
              Formatters let you modify field values — for example, by formatting dates or removing spaces. Learn more
              about formatters{' '}
              <a href="https://docs.cradl.ai/core-concepts/ai-model/formatters" className="text-primary-600">
                here
              </a>
              .
            </Text>
          </BasicTooltip>

          <span className="grow" />

          {hasFormatters ? (
            <DialogTrigger>
              <Button className="text-gray-600 hover:bg-gray-50" variant="plain">
                <AddIcon />
              </Button>
              <NewFormatterModal fieldId={fieldId} />
            </DialogTrigger>
          ) : null}
        </Flex>

        <Table
          className="border-separate border-spacing-x-0 border-spacing-y-2 px-4"
          ref={formatterTableRef}
          columns={formatterColumns}
          data={formatterData}
          hideHeader
          onRowIndexChange={(fromIndex, toIndex) => {
            moveFormatter(field.id, fromIndex, toIndex, field.parentId);
            formatterTableRef.current?.remountSortableContext();
          }}
          deleteRowAction={(row) => deleteFormatter(field.id, row.original.id, field.parentId)}
        />

        {!hasFormatters ? (
          <DialogTrigger>
            <Button className="mx-4 mt-3" variant="secondary">
              <AddIcon />
              <Text>Add formatter</Text>
            </Button>
            <NewFormatterModal fieldId={fieldId} />
          </DialogTrigger>
        ) : null}
      </Flex>
    </section>
  );
};

export const FormattersSection = memo(IFormattersSection) as typeof IFormattersSection;
